<!-- ResizableTable.svelte -->
<script lang="ts">
  import { cn } from "$lib/utils";
  import { Button } from "$lib/components/ui/button";
  import { Plus } from "lucide-svelte";
  import type { ColumnDefinition, TableState, CellSelection } from "./types";
  import type { SearchResult } from "$lib/types";
  import { createEventDispatcher, onDestroy } from "svelte";
  import TableHeader from "./tableHeader.svelte";
  import TableRow from "./tableRow.svelte";
  import LoadingRow from "./loadingRow.svelte";
  import { ChevronRight, LoaderCircle, ArrowBigDown } from "lucide-svelte";
  import { onMount } from "svelte";
  import { hotkeysStore } from "$lib/stores/hotkeysStore";
  import { searchFormOverlayStore } from "$lib/stores/searchFormOverlayStore";

  const dispatch = createEventDispatcher();

  let {
    isLoadingData = false,
    isLoadingMoreResults,
    isSearchInProgress,
    filteredResults = [],
    type = "person",
    selectedIndex = $bindable<number | null>(null),
    currentSortField = $bindable("name"),
    currentSortDirection = $bindable<"asc" | "desc">("asc"),
    currentSearchQuery = "",
    selectedResults = $bindable(new Set<string>()),
    isAllSelected = $bindable(false),
    showDefiniteOnly = $bindable(false),
    isPanelOpen = false,
    isViewedByOwner = false,
    waitingForSearchResults = false,
    hasMoreResultsToLoad = false,
    handleFetchMore = () => {},
    upsellCallback = () => {},
  } = $props<{
    isLoadingData?: boolean;
    filteredResults: SearchResult[];
    type?: "person" | "company";
    selectedIndex?: number | null;
    currentSortField?: string;
    currentSortDirection?: "asc" | "desc";
    currentSearchQuery?: string;
    selectedResults?: Set<string>;
    isAllSelected?: boolean;
    isPanelOpen?: boolean;
    isViewedByOwner?: boolean;
    showDefiniteOnly?: boolean;
    isLoadingMoreResults?: boolean;
    waitingForSearchResults?: boolean;
    hasMoreResultsToLoad?: boolean;
    isSearchInProgress?: boolean;
    handleFetchMore?: () => void;
    loggedOutCallback?: () => void;
    upsellCallback?: () => void;
  }>();

  let selectedCell = $state<CellSelection>({
    row: null,
    col: 0,
  });

  let scrollRAF = $state<number>(0);
  let currentlyNavigating = $state<boolean>(false);
  let navigationTimeout = $state<ReturnType<typeof setTimeout>>();

  function setNavigationState() {
    currentlyNavigating = true;
    clearTimeout(navigationTimeout);
    navigationTimeout = setTimeout(() => {
      currentlyNavigating = false;
    }, 150);
  }

  onDestroy(() => {
    clearTimeout(navigationTimeout);
    cancelAnimationFrame(scrollRAF);
  });

  let loaderRef = $state<HTMLDivElement | null>(null);
  let observer: IntersectionObserver | undefined;
  const debouncedFetchMore = createDebouncedFetch(handleFetchMore);

  function createDebouncedFetch(fn: () => void, delay: number = 300): () => void {
    let timeoutId: ReturnType<typeof setTimeout>;
    let lastRun = 0;

    return () => {
      const now = Date.now();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (now - lastRun >= delay) {
        fn();
        lastRun = now;
        return;
      }

      // Otherwise, set up a delayed execution
      timeoutId = setTimeout(() => {
        fn();
        lastRun = Date.now();
      }, delay);
    };
  }

  onMount(() => {
    observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          !isLoadingMoreResults &&
          // !isSearchInProgress &&
          hasMoreResultsToLoad
        ) {
          debouncedFetchMore();
        }
      },
      {
        root: null,
        rootMargin: "80px",
        threshold: 0,
      },
    );

    if (loaderRef) {
      observer.observe(loaderRef);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  });

  $effect(() => {
    if (hasMoreResultsToLoad && !isLoadingMoreResults) {
      debouncedFetchMore();
    }
  });

  // let derivedRow = $derived(selectedCell.row = selectedIndex);
  // let derivedIndex = $derived(selectedIndex = selectedCell.row);

  function handleMouseMove(e: MouseEvent) {
    if (!isResizing) return;
    const diff = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + diff);
    columnConfigs[currentResizingColumn].width = newWidth;
    updateColumnPositions();
  }

  function handleMouseUp() {
    isResizing = false;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
    if (tableRef) tableRef.removeAttribute("data-resizing");
  }

  const fixedColumns: ColumnDefinition[] = [
    {
      key: "index",
      label: `#`,
      sortable: false,
      resizable: false,
      width: 48,
      position: 0,
      sticky: true,
      align: "left",
      minWidth: 48,
    },
    // {
    //   key: "checkbox",
    //   label: "",
    //   sortable: false,
    //   resizable: false,
    //   width: 48,
    //   position: 48,
    //   sticky: true,
    //   align: "center",
    //   minWidth: 48,
    // },
  ];

  let dynamicColumns = $state<ColumnDefinition[]>([
    {
      key: "criteria",
      label: "Criteria",
      sortable: false,
      resizable: false,
      width: 240,
      align: "left",
      className: "",
    },
    {
      key: "name",
      label: type === "person" ? "Name" : "Company",
      sortable: false,
      resizable: false,
      width: type === "person" ? 240 : 400,
      align: "left",
      className: "",
    },
    {
      key: "title",
      label: "Title",
      sortable: false,
      resizable: false,
      width: 200,
      align: "left",
      className: "",
      skip: type == "company",
    },
    {
      key: "company",
      label: "Company",
      sortable: false,
      resizable: false,
      width: 200,
      align: "left",
      className: "",
      skip: type == "company",
    },
    {
      key: "contact",
      label: "Contact",
      sortable: false,
      resizable: false,
      width: 200,
      align: "left",
      className: "",
      skip: type == "company",
    },
  ]);

  let isResizing = $state(false);
  let startX = $state(0);
  let startWidth = $state(0);
  let currentResizingColumn = $state("name");
  let columnConfigs = $derived(Object.fromEntries([...fixedColumns, ...dynamicColumns].map((col) => [col.key, col])));

  let tableRef: HTMLElement;

  function updateColumnPositions() {
    // let currentPosition = 96;
    let currentPosition = 48;
    for (let i = 0; i < dynamicColumns.length; i++) {
      const col = dynamicColumns[i];
      columnConfigs[col.key].position = currentPosition;
      currentPosition += columnConfigs[col.key].width;
    }
  }

  export function addColumn() {
    const newColumn: ColumnDefinition = {
      key: `dynamic-${dynamicColumns.length}`,
      label: `Column ${dynamicColumns.length + 1}`,
      sortable: false,
      resizable: false,
      width: 200,
      align: "left",
      className: "",
    };
    dynamicColumns = [...dynamicColumns, newColumn];
    columnConfigs[newColumn.key] = newColumn;
    // updateColumnPositions();
    if (tableRef) {
      tableRef.scrollLeft = tableRef.scrollWidth;
    }

    // updateColumnPositions();
  }

  function handleSort(field: string) {
    if (currentSortField === field) {
      currentSortDirection = currentSortDirection === "asc" ? "desc" : "asc";
    } else {
      currentSortField = field;
      currentSortDirection = "asc";
    }
    dispatch("sort", { field, direction: currentSortDirection });
  }

  function handleSelectAll(checked: boolean) {
    if (filteredResults.length === selectedResults.size) {
      selectedResults = new Set();
      isAllSelected = false;
    } else {
      selectedResults = new Set(filteredResults.map((r: SearchResult) => r.id));
      isAllSelected = true;
    }
  }

  function handleSelectRow(id: string) {
    const newResults = new Set(selectedResults);
    if (newResults.has(id)) {
      newResults.delete(id);
      isAllSelected = false;
    } else {
      newResults.add(id);
      if (newResults.size === filteredResults.length) {
        isAllSelected = true;
      }
    }
    selectedResults = newResults;
  }

  function handleKeyDown(e: KeyboardEvent) {
    if ($hotkeysStore.open || $searchFormOverlayStore.isVisible) {
      return;
    }

    if (!filteredResults.length) return;

    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
      setNavigationState();
    }

    const maxRow = filteredResults.length - 1;
    const maxCol = dynamicColumns.length - 1;

    let newRow = selectedCell.row;
    let newCol = selectedCell.col;

    switch (e.key) {
      case "Escape":
        e.preventDefault();
        dispatch("panelStateChange", { isPanelOpen: false });
        break;

      case "ArrowUp":
      case "ArrowDown":
      case "ArrowLeft":
      case "ArrowRight":
        e.preventDefault();

        // Calculate new position
        switch (e.key) {
          case "ArrowUp":
            newRow = selectedCell.row === null ? maxRow : Math.max(0, selectedCell.row - 1);
            break;
          case "ArrowDown":
            newRow = selectedCell.row === null ? 0 : Math.min(maxRow, selectedCell.row + 1);
            break;
          case "ArrowLeft":
            newCol = selectedCell.col === null ? maxCol : Math.max(0, selectedCell.col - 1);
            break;
          case "ArrowRight":
            newCol = selectedCell.col === null ? 0 : Math.min(maxCol, selectedCell.col + 1);
            break;
        }

        // Only update if position changed
        if (newRow !== selectedCell.row || newCol !== selectedCell.col) {
          selectedCell = { row: newRow, col: newCol };

          // Batch DOM operations
          if (selectedCell.row !== null) {
            // Dispatch row selection event
            dispatch("rowSelect", {
              result: filteredResults[selectedCell.row],
              index: selectedCell.row,
            });

            // Use rAF to batch scroll updates
            cancelAnimationFrame(scrollRAF);
            scrollRAF = requestAnimationFrame(() => {
              const cell = document.querySelector(
                `[data-row="${selectedCell.row}"][data-col="${selectedCell.col}"]`,
              ) as HTMLElement;

              if (cell && tableRef) {
                // Get current scroll position
                const currentScrollTop = tableRef.scrollTop;
                const currentScrollLeft = tableRef.scrollLeft;

                // Calculate ideal positions
                const idealVerticalScroll = cell.offsetTop - tableRef.clientHeight / 2 + cell.offsetHeight / 2;
                const idealHorizontalScroll = cell.offsetLeft - tableRef.clientWidth / 2 + cell.offsetWidth / 2;

                // Only scroll if necessary
                if (
                  Math.abs(currentScrollTop - idealVerticalScroll) > cell.offsetHeight ||
                  Math.abs(currentScrollLeft - idealHorizontalScroll) > cell.offsetWidth
                ) {
                  tableRef.scrollTo({
                    top: idealVerticalScroll,
                    left: idealHorizontalScroll,
                    behavior: currentlyNavigating ? "auto" : "smooth",
                  });
                }
              }
            });
          }
        }
        break;
    }
  }

  function handleRowClick(e: MouseEvent, result: SearchResult, index: number) {
    const target = e.target as HTMLElement;
    const isInteractiveElement = target.closest('button, a, [role="button"], [data-interactive]');
    if (isInteractiveElement && !(isInteractiveElement as Node).isSameNode(e.currentTarget as Node)) return;

    selectedCell = {
      row: index,
      col: selectedCell.col ?? 0,
    };

    dispatch("rowSelect", { result, index });
  }

  function handleCellClick(e: MouseEvent, result: SearchResult, rowIndex: number, cellIndex: number) {
    const target = e.target as HTMLElement;
    const isInteractiveElement = target.closest('button, a, [role="button"], [data-interactive]');
    if (isInteractiveElement && !(isInteractiveElement as Node).isSameNode(e.currentTarget as Node)) return;

    selectedCell = {
      row: rowIndex,
      col: cellIndex,
    };

    dispatch("cellSelect", { result, rowIndex, cellIndex });
  }

  onDestroy(() => {
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  });
</script>

<svelte:window on:keydown={handleKeyDown} />

<div class="z-0 flex h-full w-full flex-col overflow-hidden">
  <span class="mb-2 w-full px-6 text-right text-2xs text-theme-text-secondary md:px-4"></span>
  <div class="relative min-h-0 flex-1 overflow-auto" bind:this={tableRef}>
    <table class="w-full table-fixed border-collapse">
      <TableHeader
        bind:showDefiniteOnly
        {fixedColumns}
        dynamicColumns={dynamicColumns.filter((col) => !col.skip)}
        {columnConfigs}
        {currentSortField}
        {currentSortDirection}
        {isAllSelected}
        {isLoadingData}
        onSort={handleSort}
        onSelectAll={handleSelectAll}
        onStartResize={() => {}}
      />

      <tbody>
        {#if (filteredResults.length == 0 && isLoadingData) || waitingForSearchResults}
          {#each Array(9) as _}
            <LoadingRow {fixedColumns} dynamicColumns={dynamicColumns.filter((col) => !col.skip)} {columnConfigs} />
          {/each}
        {:else}
          {#each filteredResults as result, rowIndex}
            <TableRow
              {result}
              {rowIndex}
              {selectedCell}
              {fixedColumns}
              dynamicColumns={dynamicColumns.filter((col) => !col.skip)}
              {columnConfigs}
              {currentSearchQuery}
              {type}
              {isViewedByOwner}
              {selectedResults}
              {upsellCallback}
              onSelectRow={handleSelectRow}
              onRowClick={handleRowClick}
              onCellClick={handleCellClick}
            />
          {/each}

          {#if isLoadingMoreResults || isSearchInProgress}
            {#each Array(3) as _}
              <LoadingRow {fixedColumns} dynamicColumns={dynamicColumns.filter((col) => !col.skip)} {columnConfigs} />
            {/each}
          {/if}
        {/if}
      </tbody>
    </table>
    <div bind:this={loaderRef} class="h-1 w-full" aria-hidden="true">
      <!-- {#if isLoadingMoreResults || waitingForSearchResults}
        <div class="flex w-full items-center justify-center py-4">
          <LoaderCircle class="h-5 w-5 animate-spin text-theme-text-secondary" />
        </div>
      {/if} -->
    </div>
  </div>
</div>

<style>
  :global([data-resizing="true"]) {
    user-select: none;
  }
</style>
